import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { UserRole } from '@/types/database'
import { roleDashboard } from '@/routes/roleDashboard'
import { AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

// ─── Props ────────────────────────────────────────────────────────────────────

interface RoleRouteProps {
  children: ReactNode
  /** A single role or an array of roles that are permitted to access this route. */
  role: UserRole | UserRole[]
}


// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Restricts access to a route based on the authenticated user's role.
 *
 * - While profileLoading is true (profile fetch in flight), shows a spinner.
 * - If the user's role IS in the allowed set, renders `children`.
 * - If the user's role is NOT in the allowed set, redirects to their dashboard.
 * - If profile could not be loaded (fetch done but profile is null), shows a
 *   recovery screen with a reload button.
 */
function RoleRoute({ children, role }: RoleRouteProps) {
  const { profile, profileLoading } = useAuth()

  // Profile fetch still in flight — don't flash the error screen.
  if (profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Profile fetch completed but returned null (DB error, missing row, RLS block).
  if (!profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-lg font-semibold">Não foi possível carregar seu perfil</h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          Ocorreu um erro ao buscar seus dados. Verifique o console para mais
          detalhes e tente recarregar a página.
        </p>
        <Button onClick={() => window.location.reload()}>Recarregar</Button>
      </div>
    )
  }

  const allowedRoles: UserRole[] = Array.isArray(role) ? role : [role]
  const userRole = profile.role as UserRole

  // Permitted — render the route.
  if (allowedRoles.includes(userRole)) {
    return <>{children}</>
  }

  // Known role but wrong section — soft-redirect to the user's own dashboard.
  if (userRole in roleDashboard) {
    return <Navigate to={roleDashboard[userRole]} replace />
  }

  // Unknown / misconfigured role — show a hard 403 message.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-4xl font-bold">403</h1>
      <p className="text-lg text-muted-foreground">
        Você não tem permissão para acessar esta página.
      </p>
      <a
        href="/"
        className="text-sm font-medium text-primary underline-offset-4 hover:underline"
      >
        Voltar para a página inicial
      </a>
    </div>
  )
}

export { RoleRoute }
export type { RoleRouteProps }
